import { useEffect, useState } from "react";
import { CardWithCollapse } from "../CardWithCollapse/CardWithCollapse";
import { ShipmentCustomerDetails } from "./ShipmentCustomerDetails";
import { ShipmentItems } from "./ShipmentItems";
import { DeliveryRatesComparison } from "./DeliveryRatesComparision";
import { useGetQuote } from "../../hooks/useGetQuote";
import { useCustomPackage } from "../../hooks/useCustomPackage";
import { useGetSuburbOrPostalCode } from "../../hooks/useGetSuburbOrPostalCode";
import { Box, Stack, Typography } from "@mui/material";
import { globalTheme } from "../../styles";

export const CreateShipment = ({ CustomershipmentData, orderId, data, onValidation, onError, onClose }) => {
  const [customerData, setCustomerData] = useState({
    name: CustomershipmentData.name || "",
    companyName: CustomershipmentData.companyName || "",
    phoneNumber: CustomershipmentData.phoneNumber || "",
    email: CustomershipmentData.email || "",
    addressLine1: CustomershipmentData.addressLine1 || "",
    addressLine2: CustomershipmentData.addressLine2 || "",
    country: CustomershipmentData.country || "",
    city: CustomershipmentData.city || "",
    postalCode: CustomershipmentData.postalCode || "",
    stateOrProvince: CustomershipmentData.stateOrProvince || "",
  });
  const [postCodeOrSuburb, setPostCodeOrSuburb] = useState<string | number>(customerData.city || customerData.postalCode);
  const [field, setField] = useState();

  const [selectedPackage, setSelectedPackage] = useState(null);
  const [cards, setCards] = useState([]);
  const [expandedCards, setExpandedCards] = useState([]);
  const [isSuburbOrPostcodeMatch, setIsSuburbOrPostcodeMatch] = useState(true);
  const [disableGetQuote, setDisableGetQuote] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [enabledSuburbOrPostalCode, setEnabledSuburbOrPostalCode] = useState(false);

  const { CustomPackageResponse, refetchCustomPackage } = useCustomPackage({ enabled: true });
  const { SuburbOrPostalCodeResponse, refetchSuburbOrPostalCode } = useGetSuburbOrPostalCode({
    enabled: enabledSuburbOrPostalCode,
    suburbOrPostalCode: { suburbOrPostcode: postCodeOrSuburb },
  });

  useEffect(() => {
    setEnabledSuburbOrPostalCode(true);
  });

  useEffect(() => {
    if (SuburbOrPostalCodeResponse?.result && customerData.country === "AU") {
      const suburbExists = SuburbOrPostalCodeResponse.result.some(
        (response) =>
          response.Town.toLowerCase() === customerData.city.toLowerCase() && response.Postcode === customerData.postalCode
      );

      setIsSuburbOrPostcodeMatch(suburbExists);
    } else if (customerData.country !== "AU") {
      setIsSuburbOrPostcodeMatch(true);
    }
  }, [SuburbOrPostalCodeResponse, customerData.city, customerData.postalCode]);

  useEffect(() => {
    const isValidEmail = customerData.email.includes("@") && customerData.email.includes(".");
    setEmailError(!isValidEmail);
  }, [customerData.email]);

  useEffect(() => {
    if (
      !isSuburbOrPostcodeMatch ||
      customerData.addressLine2.length > 40 ||
      customerData.addressLine1.length > 40 ||
      customerData.companyName.length > 40 ||
      customerData.name.length > 40 ||
      emailError
    ) {
      setDisableGetQuote(true);
    } else {
      setDisableGetQuote(false);
    }
  }, [isSuburbOrPostcodeMatch, customerData, emailError]);

  useEffect(() => {
    if (CustomPackageResponse?.length > 0) {
      setSelectedPackage(CustomPackageResponse[0]);
    }
  }, [CustomPackageResponse]);

  const handleInputChange = (field, value) => {
    setPostCodeOrSuburb(value);
    setField(field);

    setCustomerData((prevData) => ({ ...prevData, [field]: value }));
  };

  useEffect(() => {
    if ((customerData.country === "AU" && field === "city") || (customerData.country === "AU" && field === "postalCode")) {
      const timeoutId = setTimeout(() => {
        refetchSuburbOrPostalCode();
        setEnabledSuburbOrPostalCode(true);
      }, 1000);

      // Clean up timeout if effect is rerun before 1 seconds
      return () => clearTimeout(timeoutId);
    }
  }, [customerData.postalCode, customerData.country, customerData.city]);

  useEffect(() => {
    const newCards = [
      {
        title: "Customer Details",
        customerDetails: [
          {
            id: 1,
            value: customerData.name,
            label: "Customer Name",
            name: "name",
            type: "text",
            error: customerData.name.length > 40,
            onChange: (e) => handleInputChange("name", e.target.value),
          },
          {
            id: 2,
            value: customerData.companyName,
            label: "Company Name",
            name: "companyName",
            type: "text",
            error: customerData.companyName.length > 40,
            onChange: (e) => handleInputChange("companyName", e.target.value),
          },
          {
            id: 3,
            value: customerData.phoneNumber,
            label: "Phone Number",
            name: "phoneNumber",
            type: "text",
            onChange: (e) => handleInputChange("phoneNumber", e.target.value),
          },
          {
            id: 4,
            value: customerData.email, // Bind the value of email from the state
            label: "Email",
            name: "email",
            type: "email",
            error: emailError, // Show error if the email is invalid
            onChange: (e) => handleInputChange("email", e.target.value), // Update state on input change
          },
          {
            id: 5,
            value: customerData.addressLine1,
            label: "Address Line 1",
            name: "addressLine1",
            type: "text",
            error: customerData.addressLine1.length > 40,
            onChange: (e) => handleInputChange("addressLine1", e.target.value),
          },
          {
            id: 6,
            value: customerData.addressLine2,
            label: "Address Line 2",
            name: "addressLine1",
            type: "text",
            error: customerData.addressLine2.length > 40,
            onChange: (e) => handleInputChange("addressLine2", e.target.value),
          },
          {
            id: 7,
            value: customerData.stateOrProvince,
            label: "State",
            name: "stateOrProvince",
            type: "text",
            onChange: (e) => handleInputChange("stateOrProvince", e.target.value),
          },
          {
            id: 8,
            value: customerData.country,
            label: "Country",
            name: "country",
            type: "text",
            onChange: (e) => handleInputChange("country", e.target.value),
          },
          {
            id: 9,
            value: customerData.city,
            label: "Suburb",
            name: "city",
            type: "text",
            error: !isSuburbOrPostcodeMatch,
            helperText: !isSuburbOrPostcodeMatch && "Suburb & Postcode mismatch",
            onChange: (e) => handleInputChange("city", e.target.value),
          },
          {
            id: 10,
            value: customerData.postalCode,
            label: "Postal Code",
            name: "postalCode",
            type: "text",
            error: !isSuburbOrPostcodeMatch,
            helperText: !isSuburbOrPostcodeMatch && "Suburb & Postcode mismatch",
            onChange: (e) => handleInputChange("postalCode", e.target.value),
          },
        ],
      },
      {
        title: "Shipment Items",
        shipmentsItems: [
          { name: "reference", label: "Reference", value: "" },
          { name: "quantity", label: "Quantity", value: selectedPackage?.quantity || "" },
          { name: "weight", label: "Weight (kg)", value: selectedPackage?.weight || "" },
          { name: "length", label: "L (cm)", value: selectedPackage?.length || "" },
          { name: "width", label: "W (cm)", value: selectedPackage?.width || "" },
          { name: "height", label: "H (cm)", value: selectedPackage?.height || "" },
        ],
        menuItems: CustomPackageResponse?.map((pkg) => ({
          value: pkg.packageName,
          label: pkg.packageName,
          package: pkg,
        })),
      },
      {
        title: "Delivery Rates Comparison",
        deliveryRatesComparison: [],
      },
    ];
    setCards(newCards);
    setExpandedCards(newCards.map(() => true));
  }, [customerData, selectedPackage, CustomPackageResponse, isSuburbOrPostcodeMatch]);

  const toggleExpand = (cardIndex) => {
    setExpandedCards((prevExpandedCards) =>
      prevExpandedCards?.map((expanded, index) => (index === cardIndex ? !expanded : expanded))
    );
  };

  const { getQuoteResponse } = useGetQuote({ getQuoteData: null, enabled: false });
  const isQuoteSuccess = getQuoteResponse?.success === true;

  const handleSuburbSelect = (selectedTown, State, Postcode) => {
    setCustomerData((prevData) => ({
      ...prevData,
      city: selectedTown,
      postalCode: Postcode,
      stateOrProvince: State,
    }));
  };
  return (
    <div>
      {cards.map((card, cardIndex) => (
        <div
          style={{
            marginBottom: "16px",
            display: card.title === "Delivery Rates Comparison" && !isQuoteSuccess ? "none" : "block",
          }}
          key={cardIndex}
        >
          <CardWithCollapse title={card.title} isExpanded={expandedCards[cardIndex]} onToggle={() => toggleExpand(cardIndex)}>
            {card.customerDetails && (
              <>
                <ShipmentCustomerDetails shipments={card.customerDetails} />
                {!isSuburbOrPostcodeMatch && (
                  <Box
                    mt={2}
                    sx={{
                      border: "1px solid",
                      borderColor: globalTheme.border,
                      borderRadius: "8px",
                      padding: "10px",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "16px", fontWeight: "bold", paddingBottom: "10px", color: globalTheme.primaryColor }}
                    >
                      Address Search Results
                    </Typography>
                    <Stack spacing={1} sx={{ maxHeight: "150px", overflow: "scroll" }}>
                      {SuburbOrPostalCodeResponse?.result?.map((a, key) => (
                        <Stack
                          direction="row"
                          spacing={5}
                          padding={1}
                          key={key}
                          sx={{
                            fontSize: "12px",
                            fontWeight: "300",
                            backgroundColor: globalTheme.background,
                            borderRadius: "6px",
                            cursor: "pointer",
                          }}
                          alignItems="center"
                          onClick={() => handleSuburbSelect(a.Town, a.State, a.Postcode)}
                        >
                          <Typography sx={{ width: "150px" }}>{a.Town}</Typography>
                          <Typography sx={{ width: "50px" }}>{a.State}</Typography>
                          <Typography sx={{ width: "50px" }}>{a.Postcode}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                )}
              </>
            )}
            {selectedPackage && card.shipmentsItems && (
              <ShipmentItems
                fieldsConfig={card.shipmentsItems}
                menuItems={card.menuItems || []}
                onPackageSelect={(packageData) => setSelectedPackage(packageData)}
                CustomerDetails={customerData}
                disableGetQuote={disableGetQuote}
                data={data}
              />
            )}
            {isQuoteSuccess && card.deliveryRatesComparison && (
              <DeliveryRatesComparison
                getQuoteResponse={getQuoteResponse}
                CustomerDetails={customerData}
                orderId={orderId}
                customershipmentData={data}
                onValidation={onValidation}
                onError={onError}
                onClose={onClose}
              />
            )}
          </CardWithCollapse>
        </div>
      ))}
    </div>
  );
};
